import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LockKey, Eye, EyeSlash, SignOut } from "@phosphor-icons/react";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { useAuth } from "../lib/auth";
import { formatApiError } from "../lib/utils-extra";
import { toast } from "sonner";
import api from "../lib/api";

export default function ChangePassword() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const forced = !!user?.must_change_password;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (next.length < 8) { setErr("New password must be at least 8 characters."); return; }
    if (next !== confirm) { setErr("New password and confirmation do not match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      await refresh();
      toast.success("Password updated");
      navigate(user?.role === "super_admin" ? "/admin/dashboard" : "/dashboard");
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-6" data-testid="change-password-page">
      <Card className="w-full max-w-md surface-card border-[#E2E8F0]">
        <CardContent className="p-8 sm:p-10">
          <div className="w-12 h-12 rounded-2xl bg-[#0EA5E9] flex items-center justify-center mb-5">
            <LockKey size={24} weight="duotone" color="white" />
          </div>
          <h2 className="font-display text-2xl font-semibold mb-1">
            {forced ? "Set a new password" : "Change password"}
          </h2>
          <p className="text-sm text-[#475569] mb-8">
            {forced
              ? "For security, you must change your temporary password before continuing."
              : "Update your account password."}
          </p>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label htmlFor="current" className="small-label">
                {forced ? "Temporary password" : "Current password"}
              </Label>
              <Input
                id="current" type={show ? "text" : "password"} value={current}
                onChange={(e) => setCurrent(e.target.value)} required
                data-testid="current-password-input" className="mt-2 h-11"
              />
            </div>
            <div>
              <Label htmlFor="new" className="small-label">New password</Label>
              <div className="relative mt-2">
                <Input
                  id="new" type={show ? "text" : "password"} value={next}
                  onChange={(e) => setNext(e.target.value)} required
                  data-testid="new-password-input" className="h-11 pr-12"
                  placeholder="At least 8 characters"
                />
                <button
                  type="button" onClick={() => setShow(!show)}
                  data-testid="toggle-new-password"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#475569]"
                >
                  {show ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="confirm" className="small-label">Confirm new password</Label>
              <Input
                id="confirm" type={show ? "text" : "password"} value={confirm}
                onChange={(e) => setConfirm(e.target.value)} required
                data-testid="confirm-password-input" className="mt-2 h-11"
              />
            </div>

            {err && (
              <div className="text-sm text-[#DC2626] bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="change-password-error">
                {err}
              </div>
            )}

            <Button
              type="submit" disabled={busy}
              data-testid="change-password-submit"
              className="w-full h-11 bg-[#0EA5E9] hover:bg-[#0284C7] text-white rounded-xl"
            >
              {busy ? "Updating…" : "Update password"}
            </Button>
          </form>

          <button
            type="button"
            onClick={() => {
              if (forced) { logout(); navigate("/login"); }
              else { navigate(user?.role === "super_admin" ? "/admin/dashboard" : "/dashboard"); }
            }}
            data-testid="change-password-cancel"
            className="mt-6 w-full flex items-center justify-center gap-2 text-sm text-[#475569] hover:text-[#0F172A]"
          >
            {forced ? (<><SignOut size={16} /> Sign out instead</>) : "Cancel"}
          </button>
        </CardContent>
      </Card>
    </div>
  );
}
